import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Modal, Select } from '@components/common';
import Table from '@components/common/Table';
import AlarmManagementForm from '@components/ModalComponents/AlarmManagementForm';
import FileTypeSelectionForm from '@components/ModalComponents/FileTypeSelectionForm';

import {
	useMutateExportAlarmManagementOran,
} from '@hooks/alarms/alarmManagamentData/useMutateExportAlarmManagementOran';
import { useMutateExportBenetelAlarms } from '@hooks/alarms/alarmManagamentData/useMutateExportBenetelAlarms';
import { useMutateUpdateBenetelAlarm } from '@hooks/alarms/alarmManagamentData/useMutateUpdateBenetelAlarm';
import { useMutateUpdateOranAlarm } from '@hooks/alarms/alarmManagamentData/useMutateUpdateOranAlarm';
import { useQueryAlarmBenetel } from '@hooks/alarms/alarmManagamentData/useQueryAlarmBenetel';
import { useQueryAlarmOran } from '@hooks/alarms/alarmManagamentData/useQueryAlarmOran';
import { useQueryAlarmsListRanComplaint } from '@hooks/alarms/alarmManagamentData/useQueryAlarmsListRanComplaint';
import { useAlarmManagementFilters } from '@hooks/alarms/alarmManagementFilters/useAlarmManagementFilters';
import { useQueryOranCompliantFilters } from '@hooks/alarms/alarmManagementFilters/useQueryOranCompliantFilters';
import { useMutateUpdateAlarm } from '@hooks/alarms/useMutateUpdateAlarm';
import { useQueryAlarmVendors } from '@hooks/alarms/useQueryAlarmVendors';
import { useWindowHeight } from '@hooks/utils/useWindowHeight';

import { IAlarmManagement } from '@interfaces/alarms/alarm';
import { IAlarmSettingsDTO } from '@interfaces/alarms/dto/alarmsDTO';
import { IExportDefaultDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { EAlarmManagementView } from '@enums/alarms';
import {
	defaultMeta,
	EVendorsLabels,
	EVendorsValues,
	TMenuActionsTypes,
	TVendorsValues,
} from '@enums/common';

import {
	exportRadioButtonsData,
	menuActionWithoutSelection,
} from '@constants/common';
import { MODAL_WIDTH_480 } from '@constants/modals';
import { CUSTOM_ALARM_ID, CUSTOM_ALARM_NO_FILE_ID, getLimenetFormFields, getOranFormFields } from './constants';

import { getAlarmManagementColumns, getOranCombinedColumns } from './columns';
import { generateAlarmsFilters, getAlarmFilterValues } from './utils';

import styles from './AlarmManagementByVendor.module.scss';

const WINDOW_HEIGHT_OFFSET = 278;

interface IAlarmManagementByVendorProps {
	selectedView: string;
}

const AlarmManagementByVendor = ({ selectedView }: IAlarmManagementByVendorProps) => {
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');
	const [activeExportAction, setActiveExportAction] = useState<TMenuActionsTypes | undefined>();
	const [selectedAlarmsIds, setSelectedAlarmsIds] = useState<string[] | null>(null);
	const [editableAlarm, setEditableAlarm] = useState<IAlarmManagement>();
	const [selectedGeneratedFilters, setSelectedGeneratedFilters] = useState<Record<string, TSelectOption>>({});
	const [selectedVendor, setSelectedVendor] = useState<TSelectOption<TVendorsValues>>(null);

	const [isAlarmManagementFormModal, setIsAlarmManagementFormModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);

	const isOranCompliantView = selectedView === EAlarmManagementView.combined;

	const { alarmVendorsOptions } = useQueryAlarmVendors();

	const vendor = selectedVendor?.value;
	const isOranVendor = vendor === EVendorsValues.oran;
	const isBenetelVendor = vendor === EVendorsValues.benetel;
	const isSelectShown = !isOranCompliantView && alarmVendorsOptions.length > 1;

	const editableAlarmVendor = useMemo(() => {
		if (!isOranCompliantView) {
			return vendor;
		}

		const alarmVendor = editableAlarm?.vendor;

		if (alarmVendor === EVendorsValues.oran) {
			return EVendorsValues.oran;
		}

		if (alarmVendor === EVendorsValues.benetel) {
			return EVendorsValues.benetel;
		}
	}, [isOranCompliantView, vendor, editableAlarm]);

	const filterValues = useMemo(() => {
		return getAlarmFilterValues(selectedGeneratedFilters);
	}, [selectedGeneratedFilters]);

	const {
		data,
		countOfRecords,
		isLoading: isLoadingRanComplaint,
	} = useQueryAlarmsListRanComplaint({
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		...filterValues,
		vendor: isOranCompliantView ? undefined : vendor,
	});

	const isEditableOran = editableAlarmVendor === EVendorsValues.oran;
	const isEditableBenetel = editableAlarmVendor === EVendorsValues.benetel;

	const { alarm } = useQueryAlarmOran(isEditableOran ? editableAlarm?.id : undefined);
	const { alarm: benetelAlarm } = useQueryAlarmBenetel(isEditableBenetel ? editableAlarm?.id : undefined);

	const perVendorFilters = useAlarmManagementFilters(vendor!);
	const oranCompliantFiltersData = useQueryOranCompliantFilters();

	const filtersList = isOranCompliantView
		? oranCompliantFiltersData.filters
		: perVendorFilters.filtersList;

	const isLoadingFilters = isOranCompliantView
		? oranCompliantFiltersData.isLoading
		: perVendorFilters.isLoadingFilters;

	const isLoading = isLoadingFilters || isLoadingRanComplaint;

	const clearPagination = useCallback(() => {
		setPage(defaultMeta.page);
		setPageSize(defaultMeta.pageSize);
	}, []);

	useEffect(() => {
		if (!selectedVendor && alarmVendorsOptions.length) {
			setSelectedVendor(alarmVendorsOptions[0]);
		}
	}, [selectedVendor, alarmVendorsOptions]);

	useEffect(() => {
		setSelectedGeneratedFilters({});
		clearPagination();
		setSortDirection(false);
		setSortFieldName(null);
		setEditableAlarm(undefined);
		setSearchValue('');
	}, [vendor, selectedView, clearPagination]);

	useEffect(() => {
		clearPagination();
	}, [filterValues, clearPagination]);

	const openEditAlarmForm = useCallback((alarm: IAlarmManagement) => {
		setEditableAlarm(alarm);
		setIsAlarmManagementFormModal(true);
	}, []);

	const closeEditAlarmForm = useCallback(() => {
		setEditableAlarm(undefined);
		setIsAlarmManagementFormModal(false);
	}, []);

	const toggleFileTypeSelectionModal = useCallback(() => {
		setIsOpenFileTypeSelectionModal(prevState => !prevState);
	}, []);

	const closeTypeSelectionForm = useCallback(() => setIsOpenFileTypeSelectionModal(false), []);

	const { editAlarm, isLoadingEditAlarm } = useMutateUpdateAlarm([closeEditAlarmForm]);
	const { editOranAlarm, isLoadingEditOranAlarm } = useMutateUpdateOranAlarm(
		closeEditAlarmForm,
		isEditableOran && alarm?.alarmId && typeof alarm!.alarmId === 'object' ? alarm!.alarmId.name : undefined,
	);
	const { editBenetelAlarm, isLoadingEditBenetelAlarm } = useMutateUpdateBenetelAlarm(closeEditAlarmForm);
	const { handlePreparingExport, isLoadingExportAlarmManagementOran } = useMutateExportAlarmManagementOran();
	const {
		handlePreparingExport: handlePreparingExportBenetel,
	} = useMutateExportBenetelAlarms();

	const handleEditOranAlarm = useCallback((data: IAlarmSettingsDTO) => {
		void editOranAlarm(data, alarm);
	}, [editOranAlarm, alarm]);

	const handleEditBenetelAlarm = useCallback((data: IAlarmSettingsDTO) => {
		void editBenetelAlarm(data, benetelAlarm);
	}, [editBenetelAlarm, benetelAlarm]);

	const activeExport = useCallback((data: IExportDefaultDTO) => {
		if (!isOranCompliantView && isBenetelVendor) {
			handlePreparingExportBenetel(data);
		} else {
			handlePreparingExport(data);
		}
	}, [isOranCompliantView, isBenetelVendor, handlePreparingExport, handlePreparingExportBenetel]);

	const handleExport = useCallback((alarmsIds: Array<string>, actionId: TMenuActionsTypes) => {
		setSelectedAlarmsIds(alarmsIds);
		setActiveExportAction(actionId);

		toggleFileTypeSelectionModal();
	}, [toggleFileTypeSelectionModal]);

	const columns = useMemo(() => {
		if (isOranCompliantView) {
			return getOranCombinedColumns({ openEditAlarmForm });
		}

		return getAlarmManagementColumns({ vendor: vendor!, openEditAlarmForm });
	}, [isOranCompliantView, vendor, openEditAlarmForm]);

	const onVendorChange = useCallback((option: TSelectOption<TVendorsValues>) => {
		setSelectedVendor(option);
	}, []);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const filters: TTableFilter[] = useMemo(() => {
		return generateAlarmsFilters(
			filtersList,
			selectedGeneratedFilters,
			setSelectedGeneratedFilters,
		);
	}, [filtersList, selectedGeneratedFilters]);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: searchValue,
		name: 'searchValue',
		handler: searchHandler,
		tooltipText: 'Search',
	}], [searchValue, searchHandler]);

	const editableAlarmVendorLabel = useMemo(() => {
		const alarmVendor = editableAlarm?.vendor;

		if (alarmVendor) return alarmVendor;
		if (isEditableOran) return EVendorsLabels.oran;
		if (isEditableBenetel) return EVendorsLabels.benetel;
	}, [editableAlarm, isEditableOran, isEditableBenetel]);

	const formFields = (isEditableOran || isEditableBenetel)
		? getOranFormFields(editableAlarm, editableAlarmVendorLabel)
		: getLimenetFormFields(editableAlarm);

	const isCustomAlarm =
		(isEditableOran && typeof alarm?.alarmId === 'object' && (alarm.alarmId?.name === CUSTOM_ALARM_ID || alarm.alarmId?.name === CUSTOM_ALARM_NO_FILE_ID))
		|| (isEditableBenetel && typeof benetelAlarm?.alarmId === 'object' && (benetelAlarm.alarmId?.name === CUSTOM_ALARM_ID));

	const windowHeight = useWindowHeight();

	const [tableMaxHeight, setTableMaxHeight] = useState('');

	const alarmFormProps = useMemo(() => {
		if (isEditableOran) {
			return {
				editAlarm: handleEditOranAlarm,
				editableAlarm: alarm,
				isLoadingEditAlarm: isLoadingEditOranAlarm,
			};
		}

		if (isEditableBenetel) {
			return {
				editAlarm: handleEditBenetelAlarm,
				editableAlarm: benetelAlarm,
				isLoadingEditAlarm: isLoadingEditBenetelAlarm,
			};
		}

		return {
			editAlarm,
			editableAlarm,
			isLoadingEditAlarm,
		};
	}, [
		isEditableOran,
		isEditableBenetel,
		alarm,
		benetelAlarm,
		editableAlarm,
		handleEditOranAlarm,
		handleEditBenetelAlarm,
		editAlarm,
		isLoadingEditOranAlarm,
		isLoadingEditBenetelAlarm,
		isLoadingEditAlarm,
	]);

	const shouldShowExport = !isOranCompliantView && (isOranVendor || isBenetelVendor);

	useEffect(() => {
		const maxHeight = windowHeight - WINDOW_HEIGHT_OFFSET;

		setTableMaxHeight(`${maxHeight}px`);
	}, [windowHeight]);

	return (
		<>
			<Modal
				confirmQuestion='Are you sure you want to close the window?'
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isAlarmManagementFormModal}
				onClose={closeEditAlarmForm}
				title='Edit alarm notification rule'
				width='815px'
			>
				{alarmFormProps.editableAlarm && (
					<AlarmManagementForm
						editAlarm={alarmFormProps.editAlarm}
						editableAlarm={alarmFormProps.editableAlarm as IAlarmManagement}
						fields={formFields}
						isCustomAlarm={isCustomAlarm}
						isLoadingEditAlarm={alarmFormProps.isLoadingEditAlarm}
						onClose={closeEditAlarmForm}
						setIsDirtyFormModal={setIsDirtyFormModal}
					/>
				)}
			</Modal>
			{isOpenFileTypeSelectionModal && (
				<Modal
					isOpen={isOpenFileTypeSelectionModal}
					onClose={toggleFileTypeSelectionModal}
					title='File extension to export'
					width={MODAL_WIDTH_480}
				>
					<FileTypeSelectionForm
						activeGroupTableAction={activeExportAction}
						closeModals={closeTypeSelectionForm}
						exportRadioButtonsData={exportRadioButtonsData}
						filter={{
							...filterValues,
							page,
							pageSize,
							sortDirection,
							sortFieldName,
							searchValue,
						}}
						handlePreparingExport={activeExport}
						isLoading={isLoadingExportAlarmManagementOran}
						selectedIds={selectedAlarmsIds}
						toggleModal={toggleFileTypeSelectionModal}
					/>
				</Modal>

			)}
			<div className={styles.alarmManagementTableContainer}>
				{isSelectShown && (
					<div className={styles.selectWrapper}>
						<Select
							disabled={!isSelectShown}
							handleChange={onVendorChange}
							isWithoutUseForms
							label='Vendor'
							name='vendor'
							options={alarmVendorsOptions}
							placeholder='Select vendor'
							value={selectedVendor}
							width='240px'
						/>
					</div>
				)}
				<Table
					columns={columns}
					customExportActions={menuActionWithoutSelection}
					data={data}
					filters={filters}
					handleExport={handleExport}
					isExport={shouldShowExport}
					isLoading={isLoading}
					isPinColumns
					isWithoutCheckbox
					isWithoutDeleteAction
					maxHeight={tableMaxHeight}
					pagination={pagination}
					search={search}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1]}
					tableName={isOranCompliantView ? 'O-RAN-compliant' : vendor}
				/>
			</div>
		</>
	);
};

export default memo(AlarmManagementByVendor);
